// ── What a credit balance is worth, in words ────────────────────────────────
//
// Backs the headline sub-line: "About N pieces of SEO content with AI images".
//
// ── N IS NOT CALCULATED HERE ────────────────────────────────────────────────
// The number of pieces comes from the server's GET /credits/estimate (see
// hooks/usePieceEstimate.js), the same endpoint the WordPress plugin reads.
// This file used to divide the balance itself, and so did the plan cards, the
// trial dialog and the plugin — each with its own rate or rounding — so the
// same credits showed different numbers depending on where you looked.

/**
 * Fallback price table, used only until the server's arrives.
 * Mirrors the shipped ISEO_COST_* values.
 */
export const DEFAULT_PRICING = {
  content: { small: 6, medium: 10, large: 14 },
  image: 5,
  keywords: 1,
};

/**
 * What the balance can still buy, in words — or null if it buys nothing.
 *
 * Someone holding 5 credits cannot start the cheapest article (6) but CAN
 * generate an AI image (5) or five keyword lists. Saying only "not enough
 * credits" throws away that fact at the exact moment it is most useful.
 *
 * Ordered by usefulness rather than price: an image is a more meaningful
 * suggestion than a keyword list. Mirrors affordableWith() on the server so the
 * dashboard and the plugin's error say the same thing.
 */
export function affordableWith(totalCredits, pricing = DEFAULT_PRICING) {
  const held = Math.max(0, Number(totalCredits) || 0);
  const p = pricing || DEFAULT_PRICING;
  const options = [
    { cost: Number(p.image) || DEFAULT_PRICING.image, label: (n) => `${n} AI image${n === 1 ? '' : 's'}` },
    { cost: Number(p.keywords) || DEFAULT_PRICING.keywords, label: (n) => `${n} keyword list${n === 1 ? '' : 's'}` },
  ];
  const affordable = options
    .filter((o) => o.cost > 0 && held >= o.cost)
    .map((o) => o.label(Math.floor(held / o.cost)));
  return affordable.length > 0 ? affordable.join(' or ') : null;
}

/**
 * The line under the headline balance.
 *
 * Three cases, because "approximately 0 pieces" is not a sentence anyone should
 * read, and "1 pieces" is not either:
 *
 *   cannot afford one → say so plainly, and say what the credits DO buy, so a
 *                       balance of 5 does not read as worthless
 *   exactly one       → singular
 *   more              → plural, as before
 *
 * @param {number} totalCredits live spendable balance
 * @param {number|null} pieces from GET /credits/estimate; null while unknown
 * @param {object} [pricing] server-derived per-action costs
 */
export function creditsSummaryMessage(totalCredits, pieces, pricing = DEFAULT_PRICING) {
  // Not answered yet (or the request failed): say nothing rather than guess.
  if (pieces === null || pieces === undefined || !Number.isFinite(Number(pieces))) return '';
  return piecesSummaryMessage(Number(pieces), totalCredits, pricing);
}

/**
 * The same sentence, for a piece count that has already been worked out.
 *
 * One function words the balance cards and the Settings summary, so "This
 * equals approximately N pieces" cannot drift between them.
 *
 * @param {number} pieces        the estimate to state, from GET /credits/estimate
 * @param {number} totalCredits  the credits it estimates, for the not-enough case
 * @param {object} [pricing]     server-derived per-action costs
 */
export function piecesSummaryMessage(pieces, totalCredits, pricing = DEFAULT_PRICING) {
  if (!(Number(pieces) >= 1)) {
    const alt = affordableWith(totalCredits, pricing);
    return alt
      ? `Not enough credits for content generation available. Your balance is enough for ${alt}.`
      : 'Not enough credits for content generation available.';
  }

  if (pieces === 1) return 'This equals approximately 1 piece of SEO content with AI Image';
  return `This equals approximately ${pieces.toLocaleString()} pieces of SEO content with AI Images`;
}
